/**
 * Stake sizing for a single strategy episode.
 *
 * NUMERIC POLICY (v1): IEEE-754 binary64. No platform tick rounding.
 *
 * FIXED:
 *   every wager uses `baseStake`.
 *
 * RECOVERY:
 *   First wager of an episode always uses `baseStake`.
 *   After one or more losses in the same episode:
 *     requiredStake = (accumulatedLosses + targetEpisodeProfit) / (M - 1)
 *   so a win at cash-out M recovers those losses and realizes `targetEpisodeProfit`.
 *
 * Interaction of baseStake and targetEpisodeProfit:
 *   A first-wager win nets `baseStake * (M - 1)`. That equals `targetEpisodeProfit`
 *   only when `baseStake = targetEpisodeProfit / (M - 1)`.
 *   For M=2, baseStake=1, targetEpisodeProfit=1 this holds, and the sequence is 1,2,4,8,...
 *   For M=3, baseStake=1, targetEpisodeProfit=1 a first-wager win nets 2, not 1;
 *   later recovery stakes still target +1 after accumulated losses.
 *
 * MARTINGALE:
 *   Only valid for M = 2. Attempt n uses baseStake * 2^(n-1).
 *   For M=2, baseStake=1, targetEpisodeProfit=1, MARTINGALE and RECOVERY
 *   produce the same sequence 1, 2, 4, 8, ...
 *   Do not apply classic doubling Martingale to M > 2; use RECOVERY instead.
 */

export type StakingMode = 'FIXED' | 'RECOVERY' | 'MARTINGALE';

export function assertCashOutMultiplier(targetMultiplier: number): void {
  if (!Number.isFinite(targetMultiplier) || targetMultiplier <= 1) {
    throw new Error('Cash-out multiplier M must be a finite number > 1.');
  }
}

export function recoveryStake(
  targetMultiplier: number,
  accumulatedLosses: number,
  targetEpisodeProfit: number,
): number {
  assertCashOutMultiplier(targetMultiplier);
  if (!Number.isFinite(accumulatedLosses) || accumulatedLosses < 0) {
    throw new Error('accumulatedLosses must be a finite number >= 0.');
  }
  if (!Number.isFinite(targetEpisodeProfit) || targetEpisodeProfit <= 0) {
    throw new Error('targetEpisodeProfit must be a finite number > 0.');
  }
  const stake = (accumulatedLosses + targetEpisodeProfit) / (targetMultiplier - 1);
  if (!Number.isFinite(stake) || stake <= 0) {
    throw new Error('Recovery stake must be a finite number > 0.');
  }
  return stake;
}

export function nextStake(options: {
  mode: StakingMode;
  targetMultiplier: number;
  baseStake: number;
  targetEpisodeProfit: number;
  accumulatedLosses: number;
  wagersAlreadyPlacedInEpisode: number;
}): number {
  const { mode, targetMultiplier, baseStake, targetEpisodeProfit, accumulatedLosses, wagersAlreadyPlacedInEpisode } =
    options;
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('baseStake must be a finite number > 0.');
  }
  if (mode === 'FIXED') {
    return baseStake;
  }
  if (mode === 'MARTINGALE') {
    if (targetMultiplier !== 2) {
      throw new Error('MARTINGALE staking is defined only for target M = 2. Use RECOVERY for other multipliers.');
    }
    return martingaleStake(baseStake, wagersAlreadyPlacedInEpisode + 1);
  }
  if (wagersAlreadyPlacedInEpisode < 1) {
    return baseStake;
  }
  return recoveryStake(targetMultiplier, accumulatedLosses, targetEpisodeProfit);
}

/** 1-indexed stake[k] and prefixStaked[k] = sum of the first k stakes. */
export interface RecoverySchedule {
  stakes: number[];
  prefixStaked: number[];
  maxStakePrefix: number[];
}

export function buildRecoverySchedule(options: {
  targetMultiplier: number;
  baseStake: number;
  targetEpisodeProfit: number;
  maxAttempts: number;
  mode?: StakingMode;
}): RecoverySchedule {
  const { targetMultiplier, baseStake, targetEpisodeProfit, maxAttempts } = options;
  const mode = options.mode ?? 'RECOVERY';
  if (!Number.isInteger(maxAttempts) || maxAttempts < 0) {
    throw new Error('maxAttempts must be an integer >= 0.');
  }
  const stakes = [0];
  const prefixStaked = [0];
  const maxStakePrefix = [0];
  let accumulatedLosses = 0;
  let maxStake = 0;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const stake = nextStake({
      mode,
      targetMultiplier,
      baseStake,
      targetEpisodeProfit,
      accumulatedLosses,
      wagersAlreadyPlacedInEpisode: attempt - 1,
    });
    maxStake = Math.max(maxStake, stake);
    stakes.push(stake);
    prefixStaked.push((prefixStaked[attempt - 1] ?? 0) + stake);
    maxStakePrefix.push(maxStake);
    accumulatedLosses += stake;
  }
  return { stakes, prefixStaked, maxStakePrefix };
}

export function recoverySuccessTotals(
  schedule: RecoverySchedule,
  attempts: number,
  targetMultiplier: number,
): { totalStaked: number; grossReturned: number; netPnl: number; largestWager: number } {
  if (attempts < 1) {
    throw new Error('A successful episode must contain at least one wager.');
  }
  const totalStaked = schedule.prefixStaked[attempts];
  const winStake = schedule.stakes[attempts];
  if (totalStaked === undefined || winStake === undefined) {
    throw new Error(`Recovery schedule missing attempt ${attempts}.`);
  }
  const prior = attempts === 1 ? 0 : (schedule.prefixStaked[attempts - 1] ?? 0);
  return {
    totalStaked,
    grossReturned: winStake * targetMultiplier,
    netPnl: -prior + winStake * (targetMultiplier - 1),
    largestWager: schedule.maxStakePrefix[attempts] ?? 0,
  };
}

export function martingaleStake(baseStake: number, attempt: number): number {
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('baseStake must be a finite number > 0.');
  }
  if (!Number.isInteger(attempt) || attempt < 1) {
    throw new Error('Martingale attempt must be an integer >= 1.');
  }
  return baseStake * 2 ** (attempt - 1);
}

export function fullStopExposure(options: {
  mode: StakingMode;
  targetMultiplier: number;
  entryDrought: number;
  stopDrought: number;
  baseStake: number;
  targetEpisodeProfit: number;
}): number {
  const losingWagers = options.stopDrought - options.entryDrought;
  if (losingWagers < 1) {
    throw new Error('Stop drought S must be >= D + 1 so a full stop-out has at least one wager.');
  }
  const schedule = buildRecoverySchedule({
    targetMultiplier: options.targetMultiplier,
    baseStake: options.baseStake,
    targetEpisodeProfit: options.targetEpisodeProfit,
    maxAttempts: losingWagers,
    mode: options.mode,
  });
  return schedule.prefixStaked[losingWagers] ?? 0;
}

export function recoveryFailureTotals(schedule: RecoverySchedule, attempts: number): {
  totalStaked: number;
  grossReturned: number;
  netPnl: number;
  largestWager: number;
} {
  if (attempts < 1) {
    throw new Error('A failed episode must contain at least one wager.');
  }
  const totalStaked = schedule.prefixStaked[attempts];
  if (totalStaked === undefined) {
    throw new Error(`Recovery schedule missing attempt ${attempts}.`);
  }
  return {
    totalStaked,
    grossReturned: 0,
    netPnl: -totalStaked,
    largestWager: schedule.maxStakePrefix[attempts] ?? 0,
  };
}
